import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SeoHead from '@/components/feature/SeoHead';

const pipeServices = [
  {
    icon: 'ri-search-eye-fill',
    title: 'Leak Detection',
    desc: 'Advanced acoustic and thermal leak detection technology to pinpoint leaks without destructive excavation.',
    features: ['Acoustic detection', 'Thermal imaging', 'Non-invasive methods', 'Precise location mapping'],
    img: 'https://public.readdy.ai/ai/img_res/934382d59e6a511e89ac462de788a16d.jpg',
  },
  {
    icon: 'ri-tools-fill',
    title: 'Pipe Repair & Replacement',
    desc: 'Expert repair and full replacement of damaged, corroded, or burst pipes using high-quality materials.',
    features: ['Emergency pipe repair', 'Full pipe replacement', 'Copper & PVC piping', 'Pressure testing'],
    img: 'https://readdy.ai/api/search-image?query=professional%20plumber%20repairing%20and%20replacing%20copper%20pipes%20under%20sink%2C%20pipe%20fitting%20tools%2C%20clean%20plumbing%20work&width=600&height=400&seq=pipe002&orientation=landscape',
  },
  {
    icon: 'ri-drop-fill',
    title: 'Drain Cleaning',
    desc: 'Professional drain cleaning services to clear blockages and restore proper flow throughout your plumbing system.',
    features: ['Clog removal', 'Drain snaking', 'Root removal', 'Preventive cleaning'],
    img: 'https://readdy.ai/api/search-image?query=drain%20cleaning%20technician%20using%20professional%20drain%20snake%20equipment%20to%20clear%20blocked%20drain%2C%20plumbing%20service&width=600&height=400&seq=pipe003&orientation=landscape',
  },
  {
    icon: 'ri-water-flash-fill',
    title: 'Hydro-Jetting',
    desc: 'High-pressure water jetting to thoroughly clean pipes and remove stubborn buildup, grease, and mineral deposits.',
    features: ['High-pressure cleaning', 'Grease removal', 'Scale removal', 'Sewer line cleaning'],
    img: 'https://readdy.ai/api/search-image?query=hydro%20jetting%20equipment%20being%20used%20to%20clean%20sewer%20pipes%2C%20high%20pressure%20water%20cleaning%20service%2C%20professional%20plumbing&width=600&height=400&seq=pipe004&orientation=landscape',
  },
];

const warningSignsList = [
  { icon: 'ri-drop-fill', text: 'Unexplained increase in water bills' },
  { icon: 'ri-volume-up-fill', text: 'Gurgling or bubbling sounds from drains' },
  { icon: 'ri-alert-fill', text: 'Foul odors from drains or walls' },
  { icon: 'ri-water-percent-fill', text: 'Slow-draining sinks or bathtubs' },
  { icon: 'ri-home-fill', text: 'Damp spots on walls, floors, or ceilings' },
  { icon: 'ri-bug-fill', text: 'Increased pest activity near plumbing' },
  { icon: 'ri-temp-cold-fill', text: 'Low water pressure throughout property' },
  { icon: 'ri-alert-fill', text: 'Discolored or rusty water from taps' },
];

const pipeSchema = [
  {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Pipe Leak & Drainage Cleaning Services Houston TX',
    description: 'Expert pipe leak detection, repair, and comprehensive drainage cleaning in Houston TX. Advanced acoustic and thermal detection, hydro-jetting, and full pipe replacement.',
    provider: {
      '@type': 'LocalBusiness',
      name: 'Mitigation Restoration Services',
      telephone: '+18005551234',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
    },
    areaServed: { '@type': 'City', name: 'Houston' },
    serviceType: ['Pipe Leak Repair', 'Drainage Cleaning', 'Hydro-Jetting', 'Leak Detection'],
    url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/pipe-leak-drainage`,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What are the warning signs of a pipe leak?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Warning signs include unexplained increases in water bills, gurgling sounds from drains, foul odors, slow-draining sinks, damp spots on walls or ceilings, low water pressure, and discolored water from taps.',
        },
      },
      {
        '@type': 'Question',
        name: 'What is hydro-jetting?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Hydro-jetting is a high-pressure water cleaning method used to thoroughly clean pipes and remove stubborn buildup, grease, scale, and mineral deposits from sewer and drain lines.',
        },
      },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/` },
      { '@type': 'ListItem', position: 2, name: 'Services', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/services` },
      { '@type': 'ListItem', position: 3, name: 'Pipe Leak & Drainage', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/pipe-leak-drainage` },
    ],
  },
];

export default function PipeLeakDrainagePage() {
  return (
    <div className="min-h-screen">
      <SeoHead
        title="Pipe Leak & Drainage Cleaning Services Houston TX | Mitigation Restoration"
        description="Expert pipe leak detection, repair & drainage cleaning in Houston TX. Non-invasive acoustic & thermal detection, hydro-jetting, full pipe replacement. Free inspection available."
        keywords="pipe leak repair Houston, drainage cleaning Houston TX, hydro-jetting, leak detection, plumbing repair Houston"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/pipe-leak-drainage`}
        schema={pipeSchema}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=professional%20plumber%20working%20on%20pipe%20system%20in%20modern%20building%2C%20pipe%20repair%20and%20drainage%20cleaning%20service%2C%20clean%20industrial%20environment&width=1920&height=800&seq=pipehero001&orientation=landscape"
            alt="Pipe Leak and Drainage"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1f35]/90 via-[#0d1f35]/70 to-transparent"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 pt-32 pb-20">
          <span className="inline-flex items-center gap-2 bg-teal-500 text-white text-xs font-semibold px-4 py-1.5 rounded-full uppercase tracking-widest mb-5">
            <i className="ri-drop-fill"></i>
            Pipe & Drainage Services
          </span>
          <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Pipe Leak & Drainage<br />Cleaning Services
          </h1>
          <p className="text-white/80 text-base max-w-lg mb-8">
            Expert pipe leak detection, repair, and comprehensive drainage cleaning to protect your property from water damage and maintain optimal plumbing performance.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="tel:+18005551234"
              className="inline-flex items-center gap-2 bg-[#DC2626] text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call for Service
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-sm hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer whitespace-nowrap"
            >
              Schedule Inspection <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">What We Offer</span>
            <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2" style={{ fontFamily: "'Playfair Display', serif" }}>
              Comprehensive Pipe & Drainage Solutions
            </h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {pipeServices.map((svc) => (
              <div key={svc.title} className="bg-[#F8F9FA] rounded-2xl overflow-hidden flex flex-col sm:flex-row">
                <div className="sm:w-48 h-48 sm:h-auto shrink-0">
                  <img src={svc.img} alt={svc.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-6">
                  <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-teal-100 mb-3">
                    <i className={`${svc.icon} text-teal-600 text-base`}></i>
                  </div>
                  <h3 className="font-bold text-[#1E3A5F] text-base mb-2">{svc.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed mb-3">{svc.desc}</p>
                  <ul className="grid grid-cols-2 gap-1">
                    {svc.features.map((f) => (
                      <li key={f} className="flex items-center gap-1.5 text-xs text-gray-600">
                        <i className="ri-check-line text-teal-500 text-xs"></i>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-20 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div>
              <span className="text-[#DC2626] text-xs font-semibold uppercase tracking-widest">Don&apos;t Ignore</span>
              <h2 className="text-3xl font-extrabold text-[#1E3A5F] mt-2 mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Warning Signs You Need<br />Pipe or Drainage Service
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                Early detection of pipe leaks and drainage issues can save thousands in property damage. If you notice any of these warning signs, contact us immediately for a professional inspection.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {warningSignsList.map((sign) => (
                  <div key={sign.text} className="flex items-start gap-3 bg-white rounded-xl p-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-full bg-[#DC2626]/10 shrink-0">
                      <i className={`${sign.icon} text-[#DC2626] text-sm`}></i>
                    </div>
                    <p className="text-gray-700 text-xs leading-relaxed">{sign.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative rounded-2xl overflow-hidden h-96">
              <img
                src="https://readdy.ai/api/search-image?query=plumber%20inspecting%20pipes%20under%20floor%20with%20flashlight%2C%20pipe%20inspection%20service%2C%20professional%20plumbing%20assessment%2C%20clean%20environment&width=800&height=600&seq=pipewarning001&orientation=portrait"
                alt="Pipe Inspection"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute bottom-6 left-6 right-6 bg-white/95 rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#DC2626] shrink-0">
                    <i className="ri-phone-fill text-white text-sm"></i>
                  </div>
                  <div>
                    <p className="font-bold text-[#1E3A5F] text-sm">Free Inspection Available</p>
                    <p className="text-gray-500 text-xs">Call +1 (800) 555-1234 to schedule</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#1E3A5F] text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h3 className="text-2xl font-extrabold text-white mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
            Suspect a Pipe Leak or Drainage Issue?
          </h3>
          <p className="text-white/70 text-sm mb-6">Don&apos;t let a small leak become a major disaster. Our experts are ready to help.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+18005551234"
              className="inline-flex items-center justify-center gap-2 bg-[#DC2626] text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-red-700 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-fill"></i>
              Call Now
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 border-2 border-white text-white px-8 py-3.5 rounded-full font-semibold text-sm hover:bg-white hover:text-[#1E3A5F] transition-colors cursor-pointer whitespace-nowrap"
            >
              Schedule Inspection
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
